export interface BrandConfig {
  id: string;
  name: string;
  logo: string;
  footerLogo?: string;
  primaryColor: string;
  secondaryColor: string;
  description: string;
  features: string[];
  contactEmail: string;
  website: string;
  teams: string[];
  featureImages?: string[];
}

export const defaultBrand: BrandConfig = {
  id: 'finaconnect',
  name: 'FinaConnect',
  logo: 'https://fctech.cc/wp-content/uploads/2025/09/FinaConnect-Logo-2.png',
  footerLogo: 'https://fctech.cc/wp-content/uploads/2025/09/FinaConnect-Logo-1-1-e1758799361419.png',
  primaryColor: '#1aada0',
  secondaryColor: '#f0faf9',
  description: 'Streamlined merchant onboarding for global fintech solutions',
  features: [
    'Fast KYC/AML compliance',
    'Multi-currency banking',
    'Global document verification',
    'Real-time status updates'
  ],
  featureImages: [
    'https://plus.unsplash.com/premium_photo-1661549683908-b11e9855c469?auto=format&fit=crop&w=600&q=80',
    'https://images.unsplash.com/photo-1580519542036-c47de6196ba5?auto=format&fit=crop&w=600&q=80',
    'https://images.unsplash.com/photo-1521791055366-0d553872125f?auto=format&fit=crop&w=600&q=80',
    'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=600&q=80',
  ],
  contactEmail: 'support@finaconnect.com',
  website: 'https://finaconnect.com',
  teams: Array.from({ length: 10 }, (_, i) => `Team ${i + 1}`),
};

export const brands: Record<string, BrandConfig> = {
  finaconnect: defaultBrand,

  yuancapital: {
    id: 'yuancapital',
    name: 'Yuan Capital',
    logo: '/logo-yuancapital.svg',
    primaryColor: '#b91c1c',
    secondaryColor: '#fef2f2',
    description: 'Capital solutions and financial services',
    features: [],
    contactEmail: 'compliance@yuancapital.com',
    website: 'https://yuancapital.com',
    teams: ['Compliance', 'Operations', 'Risk Management', 'Technology', 'Finance', 'Client Services'],
  },

  abcbank: {
    id: 'abcbank',
    name: 'ABC Bank',
    logo: '/logo-abcbank.svg',
    primaryColor: '#dc2626',
    secondaryColor: '#fef2f2',
    description: 'Trusted banking solutions for modern commerce',
    features: [
      'Enterprise-grade security',
      'Global merchant services',
      'Traditional banking excellence',
      '24/7 dedicated support'
    ],
    contactEmail: 'merchant.services@abcbank.com',
    website: 'https://abcbank.com',
    teams: ['Compliance', 'Operations', 'Risk', 'Retail Banking', 'Commercial Banking', 'Technology'],
  },

  tgpay: {
    id: 'tgpay',
    name: 'TGPay',
    logo: '/logo-tgpay.png',
    primaryColor: '#0ea5e9',
    secondaryColor: '#f0f9ff',
    description: 'Modern payment solutions for the digital economy',
    features: [
      'Lightning-fast transactions',
      'Mobile-first design',
      'Advanced analytics',
      'Global reach'
    ],
    contactEmail: 'compliance@tgpay.co.uk',
    website: 'https://tgpay.co.uk',
    teams: ['Compliance', 'Operations', 'Risk', 'Product', 'Engineering', 'Finance'],
  },

  paytech: {
    id: 'paytech',
    name: 'PayTech Solutions',
    logo: '/logo-paytech.svg',
    primaryColor: '#2563eb',
    secondaryColor: '#eff6ff',
    description: 'Advanced payment processing for modern businesses',
    features: [
      'Instant merchant approval',
      'Global payment gateway',
      'Advanced fraud protection',
      'Real-time analytics'
    ],
    contactEmail: 'onboarding@paytech.io',
    website: 'https://paytech.io',
    teams: ['Compliance', 'Operations', 'Risk', 'Sales', 'Technology'],
  },

  bankflow: {
    id: 'bankflow',
    name: 'BankFlow',
    logo: '/logo-bankflow.svg',
    primaryColor: '#059669',
    secondaryColor: '#f0fdf4',
    description: 'Digital banking infrastructure for fintech companies',
    features: [
      'Rapid account opening',
      'Multi-currency accounts',
      'API-first banking',
      'Compliance automation'
    ],
    contactEmail: 'merchants@bankflow.com',
    website: 'https://bankflow.com',
    teams: ['Compliance', 'Operations', 'Risk', 'Engineering', 'Finance'],
  },

  finpay: {
    id: 'finpay',
    name: 'FinPay Global',
    logo: '/logo-finpay.svg',
    primaryColor: '#7c3aed',
    secondaryColor: '#f3e8ff',
    description: 'Next-generation payment processing for global merchants',
    features: [
      'Instant merchant setup',
      'Global payment acceptance',
      'Advanced fraud detection',
      'Real-time settlements'
    ],
    contactEmail: 'onboard@finpay.global',
    website: 'https://finpay.global',
    teams: ['Compliance', 'Operations', 'Risk', 'Sales', 'Technology', 'Finance'],
  },

  cryptobank: {
    id: 'cryptobank',
    name: 'CryptoBank Pro',
    logo: '/logo-cryptobank.svg',
    primaryColor: '#f59e0b',
    secondaryColor: '#fefbf2',
    description: 'Cryptocurrency-enabled banking for modern businesses',
    features: [
      'Crypto-fiat conversions',
      'DeFi integrations',
      'Multi-chain support',
      'Regulatory compliance'
    ],
    contactEmail: 'merchants@cryptobank.pro',
    website: 'https://cryptobank.pro',
    teams: ['Compliance', 'Operations', 'Risk', 'Technology', 'Finance'],
  },
};

// Short comp codes → brand id (case-insensitive, e.g. ?comp=YC or ?comp=yc)
const compCodeMap: Record<string, string> = {
  YC: 'yuancapital',
  ABC: 'abcbank',
  TGP: 'tgpay',
  PT: 'paytech',
  BF: 'bankflow',
  FP: 'finpay',
  CB: 'cryptobank',
};

export const getBrand = (brandId?: string): BrandConfig => {
  if (!brandId) return defaultBrand;
  const key = brandId.trim();
  const resolved = compCodeMap[key.toUpperCase()] ?? key.toLowerCase();
  return brands[resolved] ?? defaultBrand;
};
